import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  Events,
  Medication,
  Script,
  ScriptRepositoryService,
  ScriptType,
  WebSocketService
} from '@xmedical/core';
import { PatientStore } from './patient.store';
/**
 * PatientScriptsStore — scripts for the current patient.
 *
 * Auto-loads on patient change, owns the ScriptScriptChanged push
 * subscription, and exposes the two distinct create flows:
 *
 *   - createFromMedication(meds, type)  — medication widget; the script is
 *     created from a populated medication list. Server locks it implicitly
 *     unless type is ACUTE.
 *   - createDraft(type)                  — scripts widget; creates an empty
 *     script the caller will register with DraftsService for editing.
 *
 * update() and load() handle the draft-editor flow. PDF print is intentionally
 * not modelled here — it lives on the cross-cutting documents seam.
 */
@Injectable({
  providedIn: 'root'
})
export class PatientScriptsStore {
  private patientStore = inject(PatientStore);
  private repo = inject(ScriptRepositoryService);
  private websocket = inject(WebSocketService);

  private readonly _items = signal<Script[]>([]);
  private readonly _loading = signal<boolean>(false);
  private readonly _error = signal<string | null>(null);

  readonly items = this._items.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly count = computed(() => this._items().length);

  private currentChannel: string | null = null;
  private readonly eventName = Events.ScriptScriptChanged;
  // Cancels any in-flight `fetch()` HTTP when a newer request arrives.
  private fetchSub?: Subscription;

  constructor() {
    effect(() => {
      const patient = this.patientStore.current();
      const channel = this.patientStore.patientEventsChannelName();

      if (this.currentChannel && this.currentChannel !== channel) {
        this.websocket.unsubscribe(this.currentChannel, this.eventName);
        this.currentChannel = null;
      }

      if (!patient) {
        this.fetchSub?.unsubscribe();
        this._loading.set(false);
        this._items.set([]);
        this._error.set(null);
        return;
      }

      this.fetch(patient.id);

      if (channel && this.currentChannel !== channel) {
        this.websocket.subscribe(channel, this.eventName, () => this.reload());
        this.currentChannel = channel;
      }
    });
  }

  reload(): void {
    const patient = this.patientStore.current();
    if (!patient) return;
    this.fetch(patient.id);
  }

  /**
   * Create a script from a populated medication list (medication widget
   * "Save Script" flow). Returns the new script id. The server implicitly
   * locks the script unless `scriptType` is ACUTE. After success the store
   * reloads its list so the new script appears.
   */
  createFromMedication(medication: Medication[], scriptType: ScriptType): Observable<number> {
    const patient = this.requireCurrent('createFromMedication');
    return this.repo.saveScript(patient.id, medication, scriptType).pipe(
      tap({
        next: () => this.reload()
      })
    );
  }

  /**
   * Create an empty script that the caller intends to edit as a draft
   * (scripts widget "Custom Script" flow). Returns the new script id —
   * the caller is responsible for registering it with DraftsService.
   */
  createDraft(scriptType: ScriptType): Observable<number> {
    const patient = this.requireCurrent('createDraft');
    return this.repo.saveScript(patient.id, [], scriptType).pipe(
      tap({
        next: () => this.reload()
      })
    );
  }

  /**
   * Update an existing script (used by the draft editor for incremental
   * saves and the explicit lock on commit).
   */
  update(scriptId: number, script: Script): Observable<boolean> {
    return this.repo.updateScript(scriptId, script).pipe(
      tap({
        next: success => {
          if (success && script.locked) this.reload();
        }
      })
    );
  }

  /**
   * Fetch a single script by id (one-off lookup for the draft editor;
   * does not affect the list signal).
   */
  load(scriptId: number): Observable<Script> {
    return this.repo.load(scriptId);
  }

  /**
   * Delete a locked, unprinted script. Optimistically removes the item from
   * the store on success; the matching ScriptScriptChanged push event will
   * trigger a reload shortly after.
   */
  delete(scriptId: number): Observable<boolean> {
    return this.repo.delete(scriptId).pipe(
      tap({
        next: success => {
          if (success) {
            this._items.update(items => items.filter(s => s.id !== scriptId));
          }
        }
      })
    );
  }

  private fetch(patientId: number): void {
    this.fetchSub?.unsubscribe();
    this._loading.set(true);
    this._error.set(null);
    this.fetchSub = this.repo.loadPatientScripts(patientId).subscribe({
      next: scripts => {
        const sorted = [...scripts].sort((a, b) =>
          a.createdDate < b.createdDate ? 1 : -1
        );
        this._items.set(sorted);
        this._loading.set(false);
      },
      error: error => {
        this._loading.set(false);
        this._error.set('Could not load patient scripts');
        
      }
    });
  }

  private requireCurrent(operation: string) {
    const patient = this.patientStore.current();
    if (!patient) {
      throw new Error(`PatientScriptsStore.${operation}() called with no current patient`);
    }
    return patient;
  }
}
